import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import {
    Moon,
    ArrowRight,
    Clock3,
    ClipboardCheck,
    Users,
    Mail,
    Lock,
    AlertCircle,
} from "lucide-react";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const res = await login(email.trim().toLowerCase(), password);
            if (res.must_change_password) {
                navigate("/change-password", { replace: true });
            } else {
                navigate("/", { replace: true });
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="min-h-screen flex flex-col bg-[#070F26] text-white relative overflow-hidden"
            data-testid="login-page"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage:
                        "radial-gradient(1200px 500px at 15% -10%, rgba(0,114,188,0.35), transparent 60%), radial-gradient(900px 500px at 100% 20%, rgba(255,195,3,0.10), transparent 60%), radial-gradient(700px 500px at 50% 110%, rgba(0,114,188,0.18), transparent 60%)",
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    maskImage:
                        "radial-gradient(ellipse at 50% 40%, black 40%, transparent 75%)",
                }}
            />

            <header
                className="relative z-10 border-b border-white/5"
                data-testid="login-header"
            >
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0072BC] to-[#003a63] flex items-center justify-center shadow-[0_8px_22px_rgba(0,114,188,0.4)]">
                            <Moon className="h-5 w-5 text-white" />
                        </div>
                        <div className="leading-tight">
                            <div className="text-[19px] font-semibold tracking-tight">
                                NightOwl
                            </div>
                            <div className="text-[12px] text-white/60">
                                Say less, hand over more
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[12px] text-white/60">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#FFC303] shadow-[0_0_10px_rgba(255,195,3,0.8)]" />
                        24 × 7 SAP Support · Invite Only
                    </div>
                </div>
            </header>

            <main className="relative z-10 flex-1 flex items-center">
                <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-14 items-center w-full">
                    <div className="animate-fade-up">
                        <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur text-[11px] font-medium text-white/75">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#FFC303]" />
                            Clear Shifts. Clean Handoffs.
                        </div>
                        <h1
                            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-white"
                            data-testid="hero-heading"
                        >
                            Shift ends.
                            <br />
                            <span className="bg-gradient-to-r from-white via-white to-[#8FCBFF] bg-clip-text text-transparent">
                                Chaos doesn&apos;t start.
                            </span>
                        </h1>
                        <p
                            className="mt-5 text-[15px] leading-relaxed text-white/70 max-w-md"
                            data-testid="hero-subtitle"
                        >
                            NightOwl is where your 24×7 SAP team drops incidents,
                            watch items, and escalations so the next shift never
                            has to guess what happened.
                        </p>

                        <ul className="mt-8 space-y-3 max-w-md">
                            <Bullet
                                icon={ClipboardCheck}
                                text="Draft it, send it, done"
                            />
                            <Bullet
                                icon={Clock3}
                                text="Every shift covered, no cap"
                            />
                            <Bullet
                                icon={Users}
                                text="A role for literally everyone on the team"
                            />
                        </ul>
                    </div>

                    <div className="animate-fade-up lg:justify-self-end w-full max-w-[440px]">
                        <div className="relative">
                            <div
                                aria-hidden
                                className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/20 via-white/5 to-transparent"
                            />
                            <div className="relative bg-[#0B142E]/85 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
                                <div className="flex items-center gap-2 text-[12px] font-medium text-[#8FCBFF] mb-3">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#0072BC]" />
                                    Sign in
                                </div>
                                <h2
                                    className="text-2xl font-semibold text-white mb-2"
                                    data-testid="login-title"
                                >
                                    Hey, you&apos;re back.
                                </h2>
                                <p className="text-[13px] text-white/60 mb-6">
                                    NightOwl&apos;s invite only, so use whatever your
                                    admin hooked you up with.
                                </p>

                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-4"
                                    data-testid="login-form"
                                >
                                    <Field
                                        label="Email"
                                        icon={Mail}
                                        htmlFor="email"
                                    >
                                        <Input
                                            id="email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="you@company.com"
                                            className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#0072BC] focus-visible:ring-offset-0 h-11"
                                            data-testid="login-email-input"
                                            autoComplete="email"
                                        />
                                    </Field>
                                    <Field
                                        label="Password"
                                        icon={Lock}
                                        htmlFor="password"
                                    >
                                        <Input
                                            id="password"
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder="Your password"
                                            className="bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#0072BC] focus-visible:ring-offset-0 h-11"
                                            data-testid="login-password-input"
                                            autoComplete="current-password"
                                        />
                                    </Field>

                                    {error && (
                                        <div
                                            data-testid="login-error"
                                            className="flex items-start gap-2 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2"
                                        >
                                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="login-submit-btn"
                                        className="w-full h-11 bg-[#0072BC] hover:bg-[#005a94] text-white font-medium rounded-lg transition-colors"
                                    >
                                        {submitting
                                            ? "Signing in…"
                                            : "Sign in"}
                                        {!submitting && (
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        )}
                                    </Button>
                                </form>

                                <div className="mt-6 h-px bg-white/10" />
                                <p className="mt-5 text-[11px] leading-relaxed text-white/45">
                                    Locked out? Hit up your admin, they can send
                                    a new invite or reset your password.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="relative z-10 border-t border-white/5 text-white/45 text-[12px]">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <span>© NightOwl · Internal Tool</span>
                    <span>By NTT Data</span>
                </div>
            </footer>
        </div>
    );
}

function Field({ label, icon: Icon, htmlFor, children }) {
    return (
        <div>
            <Label
                htmlFor={htmlFor}
                className="text-[12px] font-medium text-white/70 mb-1.5 flex items-center gap-1.5"
            >
                <Icon className="h-3.5 w-3.5 text-white/50" />
                {label}
            </Label>
            {children}
        </div>
    );
}

function Bullet({ icon: Icon, text }) {
    return (
        <li className="flex items-start gap-3">
            <span className="mt-0.5 h-7 w-7 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center text-[#8FCBFF]">
                <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-[14px] text-white/80">{text}</span>
        </li>
    );
}
